import React, { useEffect, useRef, useState } from "react";
import { FaXmark } from "react-icons/fa6";
import {
  setLevel2Properties,
  updateLevel2Properties,
} from "../../../WorkbookSlice";
import { useDispatch } from "react-redux";
import {
  sequentialColorArray,
  getSequentialColorSceme,
} from "../../../../../Colors/ColorControlPanel";
import FourColorBoxComp from "../../Aside2Components/CustomizeComponent/FourColorBoxComp";
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from "react-icons/md";
import { FaEdit } from "react-icons/fa";
import SelectBox from "./FilterConfigCompComponents/SelectBox";

function getLuminance(r, g, b) {
  const a = [r, g, b].map((v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
}

function hexToRgb(hex) {
  const bigint = parseInt(hex.slice(1), 16);
  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255,
  };
}

function getContrastingFontColor(bgColor = "#FFFFFF") {
  // Ensure bgColor has a value, default to white if undefined
  if (!bgColor) {
    bgColor = "#FFFFFF"; // Default to white background color
  }

  let r, g, b;

  if (bgColor.startsWith("#")) {
    // If it's a hex color, convert to RGB
    ({ r, g, b } = hexToRgb(bgColor));
  } else if (bgColor.startsWith("rgb")) {
    // If it's already in RGB format, extract the values
    const rgbValues = bgColor.match(/\d+/g);
    if (rgbValues) {
      [r, g, b] = rgbValues.map(Number);
    } else {
      // If no valid RGB found, fallback to white
      [r, g, b] = [255, 255, 255];
    }
  } else {
    // If bgColor is not valid, fallback to white
    [r, g, b] = [255, 255, 255];
  }

  const luminance = getLuminance(r, g, b);

  // If luminance is lower than 0.5, use white font, otherwise black
  return luminance < 0.5 ? "White" : "Black";
}

const CustomSelect = ({ options, onSelect, colorScale }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const dropdownRef = useRef(null);
  const labelOfColorScale = getSequentialColorSceme(colorScale);
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (option) => {
    setSelectedOption(option);
    onSelect(option);
    setIsOpen(false);
  };

  // Close dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="relative w-[90%] h-full" ref={dropdownRef}>
      <div
        className="p-1 border rounded border-gray-200 cursor-pointer bg-white flex items-center justify-between"
        onClick={toggleDropdown}
      >
        {labelOfColorScale
          ? labelOfColorScale.label
          : selectedOption
          ? selectedOption.label
          : "Select a color scale"}
        {isOpen ? (
          <MdKeyboardArrowUp className="relative left-1" />
        ) : (
          <MdKeyboardArrowDown className="relative left-1" />
        )}
      </div>
      {isOpen && (
        <div className="overflow-y-auto absolute z-10 w-full mt-1 bg-white border border-gray-300 shadow h-[8rem] rounded-lg flex items-start justify-between flex-col mb-4">
          {options.map((option) => (
            <div
              key={option.value}
              className=" py-2 pl-2 cursor-pointer hover:bg-gray-200 w-full rounded flex gap-2 items-center justify-between px-2"
              onClick={() => handleOptionClick(option)}
            >
              <FourColorBoxComp colors={option.colors} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const applyColorTo = [
  {
    label: "Text",
    value: "toText",
  },
  {
    label: "Cell",
    value: "toCell",
  },
];

export default function AutoColorModal({
  closeModal,
  data,
  currentSheet,
  conditionalFormattedData,
  modalItemIndex,
  ControllerProps,
  handleModal,
  vizType,
}) {
  const {
    rename,
    measure: measureObject,
    isColorScaleAuto,
    colorScale: reduxcolorScale,
    isColorInverted: reduxIsColorInverted,
  } = conditionalFormattedData?.[modalItemIndex] || {};

  // console.log(conditionalFormattedData?.[modalItemIndex]);
  // console.log(data);

  const dispatch = useDispatch();
  const { reduxKey } = ControllerProps;
  const transformedData = data.map((item) => ({
    label: `${item.measure.value}(${item.rename})`,
    value: `${item.measure.value}_${item.rename}`,
  }));

  const [selectedValue, setSelectedValue] = useState(
    measureObject && rename
      ? `${measureObject.value}_${rename}`
      : transformedData[0].value
  );
  const [colorScale, setColorScale] = useState(
    isColorScaleAuto ? reduxcolorScale : "schemeRdYlGn"
  );
  const [colorWillApplyOn, setColorWillApplyOn] = useState("toCell");
  const [isColorInverted, setIsColorInverted] = useState(
    reduxIsColorInverted !== undefined ? reduxIsColorInverted : false
  );

  const bgColor = getSequentialColorSceme(colorScale);
  const colorArray = Array.isArray(bgColor?.colors)
    ? isColorInverted
      ? [...bgColor.colors].reverse()
      : bgColor.colors
    : [];
  const fontColors = colorArray.map((color) => getContrastingFontColor(color));

  const handleValueChange = (e) => {
    const selected = data.find(
      (item) => `${item.measure.value}_${item.rename}` === e.target.value
    );
    // console.log(selected);

    // console.log(e.target.value);

    if (selected) {
      setSelectedValue(e.target.value);
    }
  };
  const handleColorWillApplyOn = (e) => {
    setColorWillApplyOn(e.target.value);
  };

  const hanldeSelectColorScale = (option) => {
    const { value } = option;
    setColorScale(value);
  };
  const handleCheckboxChange = () => {
    setIsColorInverted(!isColorInverted); // Toggle the value of isColorInverted
  };

  const saveConditionalFormatting = () => {
    const selected = data.find(
      (item) => `${item.measure.value}_${item.rename}` === selectedValue
    );
    // console.log(selected);

    if (selected) {
      const { rename, columnName, measure, nanoId } = selected;
      const isColorScaleAuto = colorScale !== "Select a color scale";

      // Prepare the base value object
      const baseValue = {
        columnName,
        rename,
        isColorScaleAuto,
        colorWillApplyOn,
        measure,
        rulesArray: [],
        formattingModalWas: "Auto",
        isColorInverted,
        colorScale: colorScale,
        columnNanoId: nanoId,
      };
      // console.log(baseValue);

      if (modalItemIndex === -1) {
        dispatch(
          setLevel2Properties({
            reduxKey,
            Sheetid: currentSheet.Sheetid,
            parentKey: "customization",
            value: baseValue, // Send baseValue with or without colorScale
            itemInArray: true,
          })
        );
      } else {
        const newUpdatedItem = {
          ...baseValue, // Reuse the same logic for updated item
        };
        // console.log(newUpdatedItem);

        dispatch(
          updateLevel2Properties({
            reduxKey,
            Sheetid: currentSheet.Sheetid,
            parentKey: "customization",
            value: newUpdatedItem,
            itemInArray: true,
            index: modalItemIndex,
          })
        );
      }
      closeModal();
    }
  };
  // console.log(colorArray);

  const handleKeyDown = (event) => {
    if (event.ctrlKey && event.key === "Enter") {
      saveConditionalFormatting(); // Call your function here
    }
  };

  return (
    <main
      className="fixed inset-0 bg-gray-600 bg-opacity-50 flex flex-col items-center justify-center z-20"
      onKeyDown={handleKeyDown}
    >
      <main className="w-[43rem] h-auto bg-white shadow-2xl rounded-lg ">
        <header className="flex bg-gray-50 w-full p-2 items-center justify-between rounded-t-lg">
          Auto Color Scale{" "}
          <button onClick={closeModal}>
            <FaXmark />
          </button>
        </header>
        <section className="p-2 w-full flex flex-col items-center justify-center gap-4">
          <div className="w-full items-center justify-center flex gap-2">
            <div className="flex w-1/2 items-center justify-center">
              <span className="w-[4.2rem]">Field : </span>
              <SelectBox
                value={selectedValue}
                onChange={handleValueChange}
                optionsArray={transformedData}
              />
            </div>
            <div className="flex w-1/2 items-center justify-center">
              <span className="w-[40%]">Color Scale :</span>
              <CustomSelect
                options={sequentialColorArray}
                onSelect={hanldeSelectColorScale}
                colorScale={colorScale}
              />
            </div>
          </div>
          {vizType === "areamap" ? null : (
            <div className="w-full items-center justify-center flex gap-2">
              <div className="flex w-1/2 items-center justify-center">
                <span className="w-48">Apply Color on : </span>{" "}
                <SelectBox
                  value={colorWillApplyOn}
                  onChange={handleColorWillApplyOn}
                  optionsArray={applyColorTo}
                />
              </div>
              <label className="flex w-1/2 items-center justify-left">
                <input
                  type="checkbox"
                  checked={isColorInverted}
                  onChange={handleCheckboxChange}
                  className="mr-[6px] checkbox-input"
                />
                Invert
              </label>
            </div>
          )}
          <div className="w-full flex items-center justify-start border p-1">
            {colorArray.length &&
              colorArray.map((color, index) => {
                return (
                  <button
                    key={index}
                    className="w-[10%] h-[27px] border  flex items-center justify-center"
                    style={{
                      backgroundColor: color,
                      color: fontColors[index], // Apply the corresponding font color
                    }}
                  >
                    {vizType === "areamap" ? null : index}
                  </button>
                );
              })}
          </div>
        </section>
        <footer
          className={`flex w-full items-center ${
            ["areamap", "cordinatemap"].includes(vizType)
              ? "justify-end"
              : "justify-between"
          } p-2`}
        >
          {["areamap", "cordinatemap"].includes(vizType) ? null : (
            <button
              className="flex items-center gap-2"
              onClick={() => handleModal("Custom")}
            >
              <FaEdit />
              Custom
            </button>
          )}
          <div className="flex items-center justify-center gap-2">
            <button className="border w-20" onClick={closeModal}>
              Close
            </button>
            <button
              className="border w-20 bg-blue-950 text-white"
              onClick={saveConditionalFormatting}
            >
              Apply
            </button>
          </div>
        </footer>
      </main>
    </main>
  );
}
